import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { RootState } from '../../stores/store';
import backgroundImage from '../../assets/images/pvx-home-bg.png';
import { useTranslation } from 'react-i18next';
import pvxLogo from '../../assets/icons/pvx-logo-full.svg';
import TranslateIcon from '@mui/icons-material/Translate';
import { useState } from 'react';
import LanguageSelector from '../../components/SideNav/components/LanguageSelector';
import { useUserLogoutMutation } from '@api/userDetails';
import UserAuth from '@services/auth';
import useAuth from '@hooks/useAuth';

const RejectedAccess: FC = () => {
  const { t: translations } = useTranslation();
  const accessRequest = useSelector((state: RootState) => state?.UserAccessData?.accessRequest);
  const [languageSelectorAnchorEl, setLanguageSelectorAnchorEl] = useState<null | HTMLElement>(
    null,
  );

  const [userLogout] = useUserLogoutMutation();
  const { loginUrl } = useAuth();

  const logoutUser = async () => {
    await userLogout({});
    UserAuth.clearTokens();

    setTimeout(() => {
      window.location.href = loginUrl;
    }, 1000);
  };

  const companyName = (accessRequest?.data as any)?.companyName || '';

  return (
    <Box
      sx={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        height: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.25rem 2rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img src={pvxLogo} alt="PvX Partners" style={{ height: 32 }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <TranslateIcon sx={{ color: '#FFFFFF' }} />
          <Box
            sx={{
              '& .MuiButton-root': {
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: '0.875rem',
              },
              '& .MuiButton-endIcon svg': {
                color: '#FFFFFF',
              },
            }}
          >
            <LanguageSelector
              anchorEl={languageSelectorAnchorEl}
              setAnchorEl={setLanguageSelectorAnchorEl}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: 'min(560px, calc(100vw - 2rem))',
          background: '#FFFFFF',
          borderRadius: '0.75rem',
          padding: '2rem',
          color: '#162936',
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.25)',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            fontWeight: 700,
            mb: '0.75rem',
          }}
        >
          {translations('access_rejected.access_rejected')}
        </Typography>

        <Typography
          sx={{
            color: '#6C6C6C',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            mb: '1.5rem',
          }}
        >
          {translations('access_rejected.access_rejected_message', { companyName })}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', mb: '3rem' }}>
          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
            {translations('access_rejected.request_status')}
          </Typography>
          <Box
            sx={{
              padding: '0.25rem 0.75rem',
              borderRadius: '1rem',
              background: '#FFD2CC60',
              color: '#FF6347',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 700,
            }}
          >
            {translations('access_rejected.request_status_rejected')}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={logoutUser}
            sx={{
              background: '#2F736E',
              borderRadius: '5rem',
              padding: '0.75rem 2.5rem',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              '&:hover': { background: '#285F5B' },
            }}
          >
            {translations('common.sign_out')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default RejectedAccess;
